import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Button from "components/button";
import {
  getUsers,
  deleteUser,
  changeUserRole,
  adminResetPassword,
  bulkDeleteUsers,
  bulkChangeRoles,
} from "services/users";
import { backendOrigin } from "services/users";
import Pagination from "components/Pagination";
import { useToast } from "components/Toast";

const AdminUsersIndex: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 10;
  const [selected, setSelected] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<string>("id");
  const [sortDir, setSortDir] = useState<"ascending" | "descending">(
    "ascending"
  );

  const toast = useToast();

  const load = async (p = page, q = search) => {
    setLoading(true);
    try {
      // Debug: show where we'll fetch from and params
      // eslint-disable-next-line no-console
      console.debug("Loading users", {
        url: `${backendOrigin}/api/admin/users`,
        page: p,
        q,
        sortBy,
        sortDir,
      });
      const sortDirParam = sortDir === "ascending" ? "asc" : "desc";
      const data = await getUsers({
        q: q || undefined,
        page: p,
        perPage,
        sort_by: sortBy,
        sort_dir: sortDirParam,
      });
      if (Array.isArray(data)) {
        setUsers(data);
        setTotal(data.length);
      } else if (data && data.data) {
        setUsers(Array.isArray(data.data) ? data.data : []);
        setTotal(data.meta?.total ?? data.data.length);
      } else {
        setUsers([]);
        setTotal(0);
      }
    } catch (e) {
      console.error(e);
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(page, search), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    // reload when sort changes
    load(1, search);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDir]);

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete user?")) return;
    try {
      await deleteUser(id);
      // reload current page
      await load(page, search);
      toast.showSuccess("User deleted");
    } catch (e) {
      console.error(e);
      toast.showError("Failed to delete user");
    }
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selected.length === users.length) setSelected([]);
    else setSelected(users.map((u) => u.id));
  };

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Delete ${selected.length} users?`)) return;
    try {
      await bulkDeleteUsers(selected);
      setSelected([]);
      await load(page, search);
      toast.showSuccess("Users deleted");
    } catch (err) {
      console.error(err);
      toast.showError("Bulk delete failed");
    }
  };

  const handleBulkChangeRole = async (role: string) => {
    if (selected.length === 0) return;
    try {
      await bulkChangeRoles(selected, role);
      setUsers((prev) =>
        prev.map((u) => (selected.includes(u.id) ? { ...u, role } : u))
      );
      setSelected([]);
      toast.showSuccess("Roles updated");
    } catch (err) {
      console.error(err);
      toast.showError("Bulk role update failed");
    }
  };

  const toggleSort = (col: string) => {
    if (sortBy === col)
      setSortDir((d) => (d === "ascending" ? "descending" : "ascending"));
    else {
      setSortBy(col);
      setSortDir("ascending");
    }
  };

  const handleChangeRole = async (id: number, role: string) => {
    try {
      await changeUserRole(id, role);
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
      toast.showSuccess("Role updated");
    } catch (err) {
      console.error(err);
      toast.showError("Failed to update role");
    }
  };

  const handleResetPassword = async (id: number) => {
    if (!window.confirm("Send password reset email to this user?")) return;
    try {
      await adminResetPassword(id);
      toast.showSuccess("Password reset sent");
    } catch (err) {
      console.error(err);
      toast.showError("Failed to send password reset");
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-semibold">Users</h2>
        <div className="mt-3 sm:mt-0">
          <Button as="link" to="/admin/users/create">
            Create User
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          placeholder="Search users..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="input w-full sm:w-64"
        />
        <div className="text-sm text-gray-600">
          {loading ? "Loading..." : `${total} users`}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="text-sm">{selected.length} selected</div>
          <Button size="sm" variant="danger" onClick={handleBulkDelete}>
            Delete Selected
          </Button>
          <select
            onChange={(e) => handleBulkChangeRole(e.target.value)}
            className="input"
          >
            <option value="">Change role...</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <Button size="sm" variant="outline" onClick={() => setSelected([])}>
            Clear
          </Button>
        </div>
      )}

      {/* Mobile list (cards) */}
      <div className="space-y-3 md:hidden">
        {users.map((u) => (
          <div
            key={u.id}
            className="rounded-lg bg-white p-3 shadow-sm dark:bg-navy-700"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="truncate text-sm font-semibold">
                    {(u.firstname || u.name || "") +
                      (u.lastname ? " " + u.lastname : "")}
                  </div>
                  <div className="text-xs text-gray-400">#{u.id}</div>
                </div>
                <div className="mt-1 truncate text-sm text-gray-600">
                  {u.email}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <select
                    value={u.role || "user"}
                    onChange={(e) => handleChangeRole(u.id, e.target.value)}
                    className="input text-sm"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link
                to={`/admin/users/${u.id}`}
                className="text-sm text-indigo-600"
              >
                View
              </Link>
              <Button
                as="link"
                to={`/admin/users/${u.id}/edit`}
                size="sm"
                variant="outline"
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleDelete(u.id)}
              >
                Delete
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleResetPassword(u.id)}
              >
                Reset Password
              </Button>
              <label className="ml-auto flex items-center gap-1 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(u.id)}
                  onChange={() => toggleSelect(u.id)}
                />
                <span className="text-xs text-gray-500">Select</span>
              </label>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded bg-white shadow dark:bg-navy-700 dark:shadow-none md:block">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className="p-2">
                <input
                  aria-label="Select all users"
                  type="checkbox"
                  checked={selected.length === users.length && users.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th
                className="cursor-pointer p-2"
                role="button"
                tabIndex={0}
                onClick={() => toggleSort("id")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSort("id");
                  }
                }}
                aria-sort={
                  sortBy === "id"
                    ? sortDir === "ascending"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                ID{" "}
                <span className="ml-1 text-xs">
                  {sortBy === "id"
                    ? sortDir === "ascending"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="cursor-pointer p-2"
                role="button"
                tabIndex={0}
                onClick={() => toggleSort("name")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSort("name");
                  }
                }}
                aria-sort={
                  sortBy === "name"
                    ? sortDir === "ascending"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                Name{" "}
                <span className="ml-1 text-xs">
                  {sortBy === "name"
                    ? sortDir === "ascending"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th
                className="cursor-pointer p-2"
                role="button"
                tabIndex={0}
                onClick={() => toggleSort("email")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSort("email");
                  }
                }}
                aria-sort={
                  sortBy === "email"
                    ? sortDir === "ascending"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                Email{" "}
                <span className="ml-1 text-xs">
                  {sortBy === "email"
                    ? sortDir === "ascending"
                      ? "▲"
                      : "▼"
                    : "⇅"}
                </span>
              </th>
              <th className="p-2">Role</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t">
                <td className="p-2">
                  <input
                    aria-label={`Select user ${u.id}`}
                    type="checkbox"
                    checked={selected.includes(u.id)}
                    onChange={() => toggleSelect(u.id)}
                  />
                </td>
                <td className="p-2">{u.id}</td>
                <td className="p-2">
                  {(u.firstname || u.name || "") +
                    (u.lastname ? " " + u.lastname : "")}
                </td>
                <td className="p-2">{u.email}</td>
                <td className="p-2">
                  <select
                    value={u.role || "user"}
                    onChange={(e) => handleChangeRole(u.id, e.target.value)}
                    className="input"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/admin/users/${u.id}`}
                      className="text-indigo-600"
                    >
                      View
                    </Link>
                    <Button
                      as="link"
                      to={`/admin/users/${u.id}/edit`}
                      size="sm"
                      variant="outline"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDelete(u.id)}
                    >
                      Delete
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleResetPassword(u.id)}
                    >
                      Reset Password
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        perPage={perPage}
        total={total}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminUsersIndex;
